import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, X, HelpCircle } from 'lucide-react';

const empty = { question: '', answer: '', order: 0 };

const FAQManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/faqs');
      setItems(data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: items.length }); setEditing({}); };
  const openEdit = (it) => { setForm({ ...it }); setEditing(it); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing && editing.id) {
        await api.patch(`/admin/faqs/${editing.id}`, payload);
        toast.success('Question mise à jour');
      } else {
        await api.post('/admin/faqs', payload);
        toast.success('Question ajoutée');
      }
      close();
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette question ?')) return;
    try {
      await api.delete(`/admin/faqs/${id}`);
      toast.success('Question supprimée');
      load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div data-testid="admin-faq-manager">
      <div className="flex justify-between items-center mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Questions fréquentes</span>
          <h2 className="text-4xl font-serif text-[#111111]">FAQ</h2>
        </div>
        <button
          onClick={openNew}
          data-testid="faq-add-btn"
          className="flex items-center gap-3 px-6 py-3 border border-[#111111] text-xs uppercase tracking-[0.3em] hover:bg-[#333333] hover:border-[#111111] hover:text-white transition-all"
        >
          <Plus className="w-4 h-4" /> Ajouter une question
        </button>
      </div>

      {loading ? (
        <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#111111]/20">
          <HelpCircle className="w-10 h-10 text-[#6B6B6B] mx-auto mb-4" strokeWidth={1} />
          <p className="text-[#6B6B6B]">Aucune question. Cliquez sur "Ajouter".</p>
        </div>
      ) : (
        <div className="border border-[#111111]/10 bg-white">
          {items.map((it) => (
            <div key={it.id} className="border-b border-[#111111]/10 last:border-b-0 p-6 group" data-testid={`faq-item-${it.id}`}>
              <div className="flex justify-between items-start gap-6">
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#111111] mb-2">Ordre {it.order}</p>
                  <h3 className="font-serif text-xl text-[#111111] mb-3">{it.question}</h3>
                  <p className="text-sm text-[#6B6B6B] leading-relaxed line-clamp-3">{it.answer}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(it)} data-testid={`faq-edit-${it.id}`} className="w-9 h-9 bg-white flex items-center justify-center border border-[#111111]/20 hover:border-[#111111]">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(it.id)} data-testid={`faq-delete-${it.id}`} className="w-9 h-9 bg-white flex items-center justify-center border border-[#111111]/20 hover:border-red-500 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={close}>
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-2xl p-10 border-t-4 border-[#111111] relative"
            data-testid="faq-form"
          >
            <button type="button" onClick={close} className="absolute top-5 right-5 text-[#6B6B6B] hover:text-[#111111]">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-2xl font-serif mb-8 text-[#111111]">
              {editing.id ? 'Modifier la question' : 'Nouvelle question'}
            </h3>
            <div className="space-y-5">
              <Field label="Question" required>
                <input data-testid="faq-form-question" required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className="luxury-input-light" />
              </Field>
              <Field label="Réponse" required>
                <textarea data-testid="faq-form-answer" required rows={6} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className="luxury-input-light" />
              </Field>
              <Field label="Ordre d'affichage">
                <input data-testid="faq-form-order" type="number" value={form.order || 0} onChange={(e) => setForm({ ...form, order: e.target.value })} className="luxury-input-light" />
              </Field>
            </div>
            <div className="flex gap-4 mt-8">
              <button type="button" onClick={close} className="px-6 py-3 text-xs uppercase tracking-[0.3em] border border-[#111111]/30 hover:bg-[#F5F5F5]">Annuler</button>
              <button type="submit" data-testid="faq-form-submit" className="flex-1 px-6 py-3 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] transition-colors">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2 block">
      {label}{required && <span className="text-[#111111]"> *</span>}
    </span>
    {children}
  </label>
);

export default FAQManager;
